import { IJobTaskRunDetail } from "@/api/admin/admin.types";
import {
    Box,
    CircularProgress,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { CheckCircle2, ChevronRight, Circle, CircleAlert } from "lucide-react";
import {
    DerivedPhaseStatus,
    derivePhaseStatus,
    getPhaseDuration,
    getPhaseStartTime,
    isPhaseInProgress,
    PhaseDefinition,
} from "../utils";
import { LiveDuration } from "./LiveDuration";

const STATUS_CONFIG: Record<
    DerivedPhaseStatus,
    {
        icon: React.ElementType;
        color: string;
        bg: string;
        hoverBg: string;
        label: string;
    }
> = {
    completed: {
        icon: CheckCircle2,
        color: "#16a34a",
        bg: "#16a34a0a",
        hoverBg: "#16a34a18",
        label: "Completed",
    },
    failed: {
        icon: CircleAlert,
        color: "#dc2626",
        bg: "#dc26260a",
        hoverBg: "#dc262618",
        label: "Failed",
    },
    processing: {
        icon: Circle,
        color: "#d97706",
        bg: "#d977060a",
        hoverBg: "#d9770618",
        label: "Processing",
    },
    queued: {
        icon: Circle,
        color: "#9ca3af",
        bg: "transparent",
        hoverBg: "#9ca3af12",
        label: "Queued",
    },
    idle: {
        icon: Circle,
        color: "#d1d5db",
        bg: "transparent",
        hoverBg: "#d1d5db12",
        label: "Waiting",
    },
};

interface PipelineStepperProps {
    phases: PhaseDefinition[];
    groupedTasks: Map<string, IJobTaskRunDetail[]>;
    jobFailed: boolean;
    onPhaseClick: (phaseIndex: number) => void;
}

export const PipelineStepper = ({
    phases,
    groupedTasks,
    jobFailed,
    onPhaseClick,
}: PipelineStepperProps) => {
    const muiTheme = useTheme();
    const isSmall = useMediaQuery(muiTheme.breakpoints.down("sm"));

    return (
        <Stack
            direction={isSmall ? "column" : "row"}
            alignItems={isSmall ? "stretch" : "center"}
            spacing={0}
            sx={{ width: "100%" }}
        >
            {phases.map((phase, index) => {
                const tasks = groupedTasks.get(phase.key) ?? [];
                const status = derivePhaseStatus(tasks);
                const isSkipped = status === "idle" && jobFailed;
                const config = STATUS_CONFIG[status];
                const Icon = config.icon;
                const duration = getPhaseDuration(tasks);
                const liveStart = getPhaseStartTime(tasks);
                const isLive = isPhaseInProgress(tasks);
                const taskCount = tasks.length;
                const prevStatus =
                    index > 0
                        ? derivePhaseStatus(
                              groupedTasks.get(phases[index - 1].key) ?? []
                          )
                        : null;
                const connectorColor =
                    prevStatus === "completed" ? "#16a34a" : "#e5e7eb";

                return (
                    <Stack
                        key={phase.key}
                        direction={isSmall ? "column" : "row"}
                        alignItems="center"
                        sx={{ flex: 1, minWidth: 0 }}
                    >
                        {index > 0 && (
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    ...(isSmall
                                        ? {
                                              height: 24,
                                              width: 2,
                                              backgroundColor: connectorColor,
                                              mx: "auto",
                                          }
                                        : {
                                              width: 32,
                                              flexShrink: 0,
                                          }),
                                }}
                            >
                                {!isSmall && (
                                    <ChevronRight
                                        size={16}
                                        color={connectorColor}
                                    />
                                )}
                            </Box>
                        )}

                        <Box
                            onClick={() => onPhaseClick(index)}
                            sx={{
                                flex: 1,
                                minWidth: isSmall ? "auto" : 160,
                                borderRadius: "8px",
                                backgroundColor: config.bg,
                                border: "1px solid",
                                borderColor: "divider",
                                px: 2,
                                py: 1.5,
                                cursor: "pointer",
                                transition: "background-color 0.15s ease",
                                "&:hover": {
                                    backgroundColor: config.hoverBg,
                                },
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 600,
                                    color: "#233876",
                                    fontSize: "0.85rem",
                                    mb: 0.5,
                                }}
                            >
                                {phase.label}
                            </Typography>

                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={0.5}
                            >
                                {status === "processing" ? (
                                    <CircularProgress
                                        size={14}
                                        sx={{ color: config.color }}
                                    />
                                ) : (
                                    <Icon size={14} color={config.color} />
                                )}
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: config.color,
                                        fontSize: "0.7rem",
                                        fontWeight: 500,
                                    }}
                                >
                                    {isSkipped ? "Skipped" : config.label}
                                </Typography>
                            </Stack>

                            {isLive && liveStart ? (
                                <Stack
                                    direction="row"
                                    spacing={0.5}
                                    sx={{ mt: 0.25 }}
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: "text.secondary",
                                            fontSize: "0.7rem",
                                            fontVariantNumeric: "tabular-nums",
                                        }}
                                    >
                                        {`${taskCount} task${taskCount !== 1 ? "s" : ""} ·`}
                                    </Typography>
                                    <LiveDuration
                                        startTime={liveStart}
                                        variant="caption"
                                        sx={{
                                            color: "text.secondary",
                                            fontSize: "0.7rem",
                                            fontVariantNumeric: "tabular-nums",
                                        }}
                                    />
                                </Stack>
                            ) : (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: "text.secondary",
                                        fontSize: "0.7rem",
                                        mt: 0.25,
                                        display: "block",
                                        fontVariantNumeric: "tabular-nums",
                                    }}
                                >
                                    {taskCount > 0
                                        ? `${taskCount} task${taskCount !== 1 ? "s" : ""}${duration ? ` · ${duration}` : ""}`
                                        : isSkipped
                                          ? "Skipped"
                                          : "Waiting"}
                                </Typography>
                            )}
                        </Box>
                    </Stack>
                );
            })}
        </Stack>
    );
};
